import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO_URL } from "@/data/site";
import { Lock, ArrowRight } from "lucide-react";

export default function AdminLogin() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/admin/leads" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Enter email and password"); return; }
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin/leads");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Invalid credentials");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="min-h-screen bg-brand-beige flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <a href="/" className="flex items-center gap-3 justify-center mb-10 group">
          <img src={LOGO_URL} alt="DigiDoot" className="w-12 h-12 rounded-full ring-1 ring-brand-border" />
          <span className="font-display text-3xl">DigiDoot</span>
        </a>

        <div className="bg-brand-beige border border-brand-border p-8 md:p-10">
          <p className="eyebrow mb-4">Admin access</p>
          <h1 className="font-display text-4xl leading-tight mb-2 font-medium">
            Sign in to the <span className="italic text-brand-brown">studio</span>.
          </h1>
          <p className="text-sm text-brand-charcoal/70 mb-8">Manage leads from your website contact form.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.24em]">Email</Label>
              <Input
                data-testid="admin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-2"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.24em]">Password</Label>
              <Input
                data-testid="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2"
              />
            </div>
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-3 px-6 py-3 rounded-full bg-brand-gradient text-white font-semibold text-sm hover:opacity-90 shadow-lg shadow-brand-charcoal/20 transition-colors disabled:opacity-60"
            >
              {busy ? "Signing in..." : "Sign in"}
              <ArrowRight size={16} />
            </button>
          </form>
        </div>

        <p className="mt-6 text-xs text-brand-charcoal/60 text-center flex items-center justify-center gap-2">
          <Lock size={12} /> Private area. Access restricted to DigiDoot team.
        </p>
      </div>
    </main>
  );
}
